 
/**
 * Hook for syncing lead filters with URL query parameters
 *
 * This hook synchronizes Jotai atoms with URL query parameters, enabling:
 * - Page reload persistence
 * - Shareable filter URLs
 * - Browser back/forward navigation
 *
 * Uses the same parameter structure as the API for consistency
 */
import { useAtomValue,useSetAtom } from 'jotai';
import { useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';

import { CommonDateFilter as LeadsDateFilter } from '@common/types/filters';
import { parseArray, parseBoolean, parseNumber } from '@common/utils/parse';

import {
	accountFilterAtom,
	actionRequiredFilterAtom,
	currentPageAtom,
	dateCreatedFilterAtom,
	dealAmountRangeFilterAtom,
	debouncedSearchValueAtom,
	itemsPerPageAtom,
	lastUpdatedFilterAtom,
	leadTypeFilterAtom,
	pipelineFilterAtom,
	preQualStatusFilterAtom,
	stageFilterAtom,
	updatePreQualStatusFilterAtom,
	vehicleModelsFilterAtom,
} from '../atoms/leadsAtoms';

/**
 * Hook to synchronize lead filters with URL query parameters
 */
export const useLeadsFiltersUrl = () => {
	const [searchParams, setSearchParams] = useSearchParams();
	const isInitialized = useRef(false);
	const isSyncingFromUrl = useRef(false);

	// Setters for all filter atoms
	const setSearchValue = useSetAtom(debouncedSearchValueAtom);
	const setActionRequired = useSetAtom(actionRequiredFilterAtom);
	const setPipelines = useSetAtom(pipelineFilterAtom);
	const setPreQualStatus = useSetAtom(updatePreQualStatusFilterAtom);
	const setStages = useSetAtom(stageFilterAtom);
	const setLeadTypes = useSetAtom(leadTypeFilterAtom);
	const setVehicleModels = useSetAtom(vehicleModelsFilterAtom);
	const setDealAmountRange = useSetAtom(dealAmountRangeFilterAtom);
	const setAccounts = useSetAtom(accountFilterAtom);
	const setDateCreated = useSetAtom(dateCreatedFilterAtom);
	const setLastUpdated = useSetAtom(lastUpdatedFilterAtom);
	const setCurrentPage = useSetAtom(currentPageAtom);
	const setItemsPerPage = useSetAtom(itemsPerPageAtom);

	// Getters for all filter atoms (to sync to URL)
	const searchValue = useAtomValue(debouncedSearchValueAtom);
	const actionRequired = useAtomValue(actionRequiredFilterAtom);
	const pipelines = useAtomValue(pipelineFilterAtom);
	const preQualStatuses = useAtomValue(preQualStatusFilterAtom);
	const stages = useAtomValue(stageFilterAtom);
	const leadTypes = useAtomValue(leadTypeFilterAtom);
	const vehicleModels = useAtomValue(vehicleModelsFilterAtom);
	const dealAmountRange = useAtomValue(dealAmountRangeFilterAtom);
	const accounts = useAtomValue(accountFilterAtom);
	const dateCreated = useAtomValue(dateCreatedFilterAtom);
	const lastUpdated = useAtomValue(lastUpdatedFilterAtom);
	const currentPage = useAtomValue(currentPageAtom);
	const itemsPerPage = useAtomValue(itemsPerPageAtom);

	// Initialize atoms from URL on mount
	useEffect(() => {
		if (isInitialized.current) return;

		isSyncingFromUrl.current = true;

		// Parse URL params and set atoms
		const urlQuery = searchParams.get('query');
		if (urlQuery) {
			setSearchValue(urlQuery);
		}

		// Note: Tab is managed through route path (/:servicesProviderId/leads/:tabId)
		// not query parameters, so we don't sync it here

		const urlActionRequired = searchParams.get('actionRequired');
		if (urlActionRequired !== null) {
			setActionRequired(parseBoolean(urlActionRequired));
		}

		const urlPipeline = searchParams.get('pipeline');
		if (urlPipeline) {
			setPipelines(parseArray(urlPipeline));
		}

		const urlPreQualStatus = searchParams.get('preQualStatus');
		if (urlPreQualStatus) {
			setPreQualStatus(parseArray(urlPreQualStatus));
		}

		const urlStages = searchParams.get('stage');
		if (urlStages) {
			setStages(parseArray(urlStages));
		}

		const urlLeadTypes = searchParams.get('leadType');
		if (urlLeadTypes) {
			setLeadTypes(parseArray(urlLeadTypes));
		}

		const urlVehicles = searchParams.get('vehicles');
		if (urlVehicles) {
			setVehicleModels(parseArray(urlVehicles));
		}

		const urlDealAmountMin = searchParams.get('dealAmountMin');
		const urlDealAmountMax = searchParams.get('dealAmountMax');
		if (urlDealAmountMin && urlDealAmountMax) {
			setDealAmountRange({
				min: parseNumber(urlDealAmountMin, 0),
				max: parseNumber(urlDealAmountMax, 100000),
			});
		}

		const urlAccounts = searchParams.get('account');
		if (urlAccounts) {
			setAccounts(parseArray(urlAccounts));
		}

		const urlDateCreated = searchParams.get('dateCreated');
		if (
			urlDateCreated &&
			Object.values(LeadsDateFilter).includes(urlDateCreated as LeadsDateFilter)
		) {
			setDateCreated(urlDateCreated as LeadsDateFilter);
		}

		const urlLastUpdated = searchParams.get('lastUpdated');
		if (
			urlLastUpdated &&
			Object.values(LeadsDateFilter).includes(urlLastUpdated as LeadsDateFilter)
		) {
			setLastUpdated(urlLastUpdated as LeadsDateFilter);
		}

		const urlPage = searchParams.get('page');
		if (urlPage) {
			setCurrentPage(parseNumber(urlPage, 1));
		}

		const urlItemsPerPage = searchParams.get('itemsPerPage');
		if (urlItemsPerPage) {
			setItemsPerPage(parseNumber(urlItemsPerPage, 25));
		}

		isInitialized.current = true;
		isSyncingFromUrl.current = false;
		// eslint-disable-next-line react-hooks/exhaustive-deps -- intentional: run once on mount, setters are stable
	}, []); // Only run on mount - setters are stable and don't need to be in deps

	// Sync atoms to URL whenever they change
	useEffect(() => {
		// Skip if we're currently syncing from URL to avoid circular updates
		if (!isInitialized.current || isSyncingFromUrl.current) return;

		const params = new URLSearchParams();

		// Add query parameter
		if (searchValue) params.set('query', searchValue);

		// Note: Tab is managed through route path (/:servicesProviderId/leads/:tabId)
		// not query parameters, so we don't sync it here

		// Add action required filter
		if (actionRequired) params.set('actionRequired', 'true');

		// Add pipeline filters
		if (pipelines.length > 0) {
			params.set('pipeline', pipelines.join(','));
		}

		// Add prequal status filters
		if (preQualStatuses.length > 0) {
			params.set('preQualStatus', preQualStatuses.join(','));
		}

		// Add stage filters
		if (stages.length > 0) {
			params.set('stage', stages.join(','));
		}

		// Add lead type filters
		if (leadTypes.length > 0) {
			params.set('leadType', leadTypes.join(','));
		}

		// Add vehicle model filters
		if (vehicleModels.length > 0) {
			params.set('vehicles', vehicleModels.join(','));
		}

		// Add deal amount range filter
		if (dealAmountRange) {
			params.set('dealAmountMin', dealAmountRange.min.toString());
			params.set('dealAmountMax', dealAmountRange.max.toString());
		}

		// Add account filters
		if (accounts.length > 0) {
			params.set('account', accounts.join(','));
		}

		// Add date created filter
		if (dateCreated) {
			params.set('dateCreated', dateCreated);
		}

		// Add last updated filter
		if (lastUpdated) {
			params.set('lastUpdated', lastUpdated);
		}

		// Add page parameter (only if not 1)
		if (currentPage !== 1) {
			params.set('page', currentPage.toString());
		}

		// Add items per page parameter (only if not default 25)
		if (itemsPerPage !== 25) {
			params.set('itemsPerPage', itemsPerPage.toString());
		}

		// Update URL without adding to history (use replace to avoid cluttering browser history)
		setSearchParams(params, { replace: true });
		// eslint-disable-next-line react-hooks/exhaustive-deps -- intentional: sync URL when filter atoms change, setSearchParams is stable
	}, [
		searchValue,
		actionRequired,
		pipelines,
		preQualStatuses,
		stages,
		leadTypes,
		vehicleModels,
		dealAmountRange,
		accounts,
		dateCreated,
		lastUpdated,
		currentPage,
		itemsPerPage,
	]);
};
